import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";

import { useQueryClient } from "@tanstack/react-query";

import CODStatus from "./CODStatus";
import Card from "../../shared/Card/Card";
import Modal from "../../../layouts/Modal";
import useEmails from "../../../data/useEmails";
import { IVehicle } from "../../../models/Vehicle";
import useSingleLead from "../../../data/useSingleLead";
import ContactFromId from "../../contacts/ContactFromId";
import DropOffCalendar from "../DropOff/DropOffCalendar";
import formatDateTime from "../../../utils/formatDateTime";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import TripManager from "../../../pages/Calendar/TripManager";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import useNotification from "../../notifications/useNotifications";
import { ILead, LeadSource, LeadStatus } from "../../../models/Lead";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useLeads from "../../../data/useLeads";

export default function LeadStatusCard({ leadId }: { leadId: string }) {
  const { lead, update: updateLead, complete, downloadDoc, getCollectionNote } = useSingleLead(leadId, "LeadStatusCard");
  const { undropLead, toggleLeadCollectionDropOff } = useLeads(true, "LeadStatusCard");
  const { update: updateVehicle } = useVehiclesMutations();
  const queryClient = useQueryClient();

  const { sendDocumentsEmail } = useEmails();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const [collectionOnOpen, setCollectionOnOpen] = useState(false);
  const [dropOffOpen, setDropOffOpen] = useState(false);

  const [confirmComplete, setConfirmComplete] = useState(false);
  const [toggleCollectionOpen, setToggleCollectionOpen] = useState(false);
  const [collectedOpen, setCollectedOpen] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [collectionOn, setCollectionOn] = useState<string | null>(null);
  const [collectionOnSlot, setCollectionOnSlot] = useState<number>(0);
  const [collectedOn, setCollectedOn] = useState<string | null>(null);
  const [leadStatus, setLeadStatus] = useState(LeadStatus["Awaiting Contact"]);


  const isDropOff = useMemo(() => lead.data?.data.vehicle?.isDropOff, [lead.data])


  const handleSaveCollectionOn = () => {
    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      scheduledCollectionOn: collectionOn,
      scheduledCollectionSlot: collectionOnSlot
    };

    setLeadStatus(LeadStatus["Scheduled"]);
    handleUpdateVehicle(updatedVehicle);
  }

  const handleSaveCollectedOn = () => {
    const collected = lead.data?.data.vehicle?.scheduledCollectionOn ? DateTime.fromISO(lead.data?.data.vehicle?.scheduledCollectionOn) : DateTime.now();

    setCollectedOn(collected.toISODate());
    setLeadStatus(LeadStatus["Collected"]);

    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      collectedOn: collected.toISODate()
    };

    handleUpdateVehicle(updatedVehicle);
  }

  const handleClearCollectionOn = () => {
    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      scheduledCollectionOn: null,
      scheduledCollectionSlot: 0,
      collectionStart: null,
      collectionEnd: null,
    };

    const updatedLead: ILead = {
      ...lead.data?.data!,
      vehicle: updatedVehicle,
      status: LeadStatus["Awaiting Contact"]
    }

    setLeadStatus(LeadStatus["Awaiting Contact"]);

    setCollectionOn(null);
    setCollectionOnSlot(0);

    undropLead.mutate(lead.data?.data.id ?? "", {
      onSuccess: () => {
        handleUpdateVehicle(updatedVehicle);
      },
      onError: () => {
        saveHandlers.onError();
      }
    });
  }

  const handleClearCollectedOn = () => {
    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      collectedOn: null
    };

    setCollectedOn("");

    if (updatedVehicle.scheduledCollectionOn) {
      setLeadStatus(LeadStatus["Scheduled"]);
    } else {
      setLeadStatus(LeadStatus["Awaiting Contact"]);
    }

    handleUpdateVehicle(updatedVehicle);
  }

  const handleUpdateVehicle = async (vehicle: IVehicle) => {
    await updateVehicle.mutateAsync(vehicle, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setCollectionOnOpen(false);
        setCollectedOpen(false);

        //Need to wait for the query to update before invalidating lead - somehow the best way to make this work
        setTimeout(() => {
          queryClient.invalidateQueries(["lead", leadId]);
        }, 1000 * 2);
      },
      onError: () => {
        saveHandlers.onError();
      }
    })
  }

  const handleStatusChange = (status: string) => {
    const updatedLead = {
      ...lead.data?.data!,
      status: parseInt(status)
    };

    setLeadStatus(parseInt(status));

    //If changing to collected then also update the collected on date
    if (parseInt(status) === LeadStatus["Collected"]) {
      updateLead.mutate(updatedLead, {
        onSuccess: () => {
          //Only changes collectedOn if it's null
          if (!lead.data?.data.vehicle?.collectedOn) {
            handleSaveCollectedOn();
          }
          saveHandlers.onSuccess();
        },
        onError: () => {
          saveHandlers.onError();
        }
      });
    } else {
      updateLead.mutate(updatedLead, saveHandlers);
    }
  }

  const printCodCertificate = () => {
    var ids = {
      leadId: lead.data?.data.id,
      docId: lead.data?.data.vehicle?.codCertificateDocument
    };

    downloadDoc.mutate(ids, {
      onSuccess: (file: any) => {
        const url = window.URL.createObjectURL(file);
        const w = window.open(url, "_blank");
        w?.print();
      }
    });
  }

  const sendDocuments = (collectionNote: boolean, codCertificate: boolean) => {
    let body = {
      leadId: lead.data?.data.id,
      collectionNote: collectionNote,
      codCertificate: codCertificate
    }

    sendDocumentsEmail.mutate(body, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: "Documents sent",
        });
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Unable to send documents",
        });
      }
    });
  }

  const printCollectionNote = (createNew: boolean) => {
    getCollectionNote.mutate({ leadId: lead.data?.data.id!, createNew: createNew }, {
      onSuccess: (file: any) => {
        //print file
        const url = window.URL.createObjectURL(file);
        const w = window.open(url, "_blank");
        w?.print();
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Unable to print collection note",
        });
      }
    })
  }

  const handleComplete = () => {
    setCompleteLoading(true);

    complete.mutate(lead.data?.data.id!, {
      onSuccess: (res: any) => {
        setConfirmComplete(false);
        addNotification({
          variant: "success",
          primaryText: res.message,
        })
      },
      onError: (res: any) => {
        addNotification({
          variant: "error",
          primaryText: res.message,
        })
      },
      onSettled: () => {
        setCompleteLoading(false);
      }
    })
  }

  const handleToggleCollectionDropOff = () => {
    //call toggle endpoint
    toggleLeadCollectionDropOff.mutate(lead.data?.data.id!, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setToggleCollectionOpen(false);
      }
    });
  }

  const LeadStatusRow = ({ label, value }: { label: string, value: string }) => (
    <div className="py-2 grid grid-cols-2 gap-4">
      <dt className="text-sm font-medium text-gray-500">
        {label}
      </dt>
      <dd className="items-center text-sm text-gray-900">
        <div>
          {value}
        </div>
      </dd>
    </div>
  );

  useEffect(() => {
    if (lead.isLoading || !lead.data?.data) return;

    // console.log(lead.data.data)
    setLeadStatus(lead.data.data.status);
    setCollectionOn(lead.data.data.vehicle?.scheduledCollectionOn ?? "");
    setCollectionOnSlot(lead.data.data.vehicle?.scheduledCollectionSlot ?? 0);
    setCollectedOn(lead.data.data.vehicle?.collectedOn ?? "");
    setLeadStatus(lead.data.data.status);

  }, [lead.isLoading, lead.data?.data])


  return (
    <>
      <ConfirmModal open={confirmComplete} setOpen={setConfirmComplete} onConfirm={() => handleComplete()}
        title="Complete Lead" message={`Really complete this lead?\nThis will update the status to 'Complete' and send the customer a confirmation email.`}
        confirmButtonText="Complete" isLoading={completeLoading}
      />
      <ConfirmModal open={toggleCollectionOpen} setOpen={setToggleCollectionOpen} onConfirm={() => handleToggleCollectionDropOff()}
        title="Toggle Collection/Drop Off" message={`Really change this lead type?\nThis will update the status to '${isDropOff ? "Collection" : "Drop Off"}'.\nAny scheduled ${isDropOff ? "drop off" : "collection"} date will be cleared and lead will revert to Awaiting Contact.`}
        confirmButtonText="Toggle" isLoading={completeLoading}
      />
      <Modal open={collectionOnOpen} setOpen={setCollectionOnOpen} width="max-w-7xl">
        <div className="flex justify-between">
          <label>Set Collection Scheduled On:</label>

          <button
            onClick={() => handleClearCollectionOn()}
            className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
          >
            Clear
          </button>
        </div>

        {/* <Calendar vrm={lead.data?.data.vehicle?.vrm} editable={false} waiting={false} /> */}
        <TripManager />

      </Modal>

      <Modal open={dropOffOpen} setOpen={setDropOffOpen} width="max-w-7xl">
        <DropOffCalendar currentLead={lead.data?.data} />
      </Modal>

      <Card
        bodyClassName="p-4"
        title="Lead Status"
        collapsible
      >
        <div className="md:grid md:grid-cols-5 gap-3">

          <div className="col-span-5 divide-y ">
            <div className="py-2 grid grid-cols-2 gap-4 items-center">
              <dt className="text-sm font-medium text-gray-500">
                Lead Type
              </dt>
              <dd className="flex justify-between items-center text-sm text-gray-900">
                <div>
                  {isDropOff ? "Drop Off" : "Collection"}
                </div>

                <button
                  type="button"
                  onClick={() => setToggleCollectionOpen(true)}
                  className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                  Change to {isDropOff ? "Collection" : "Drop Off"}
                </button>
              </dd>
            </div>

            <div className="py-2 grid grid-cols-2 gap-4 items-center">
              <dt className="text-sm font-medium text-gray-500">
                Lead Source
              </dt>
              <dd className="items-center text-sm text-gray-900">
                <div>
                  {LeadSource[lead.data?.data.website!]}
                </div>
              </dd>
            </div>
            <div className="py-2 grid grid-cols-2 gap-4 items-center">
              <dt className="text-sm font-medium text-gray-500">
                {isDropOff ? "Drop Off" : "Collection"} Scheduled On
              </dt>
              <dd className="flex justify-between items-center text-sm text-gray-900">
                <div>
                  {collectionOn ? formatDateTime(collectionOn, DateTime.DATE_SHORT) : "Not Set"}
                </div>
                <button
                  type="button"
                  onClick={() => { isDropOff ? setDropOffOpen(true) : setCollectionOnOpen(true) }}
                  className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                  Change
                </button>
              </dd>
            </div>
            <div className="py-2 grid grid-cols-2 gap-4 items-center">
              <dt className="text-sm font-medium text-gray-500">
                {isDropOff ? "Dropped Off" : "Collected"} On
              </dt>
              <dd className="flex justify-between items-center text-sm text-gray-900">
                <div>
                  {collectedOn ? formatDateTime(collectedOn, DateTime.DATE_SHORT) : "Not Set"}
                </div>
                {collectedOn ? (
                  <button
                    type="button"
                    onClick={() => handleClearCollectedOn()}
                    className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                    Clear
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSaveCollectedOn()}
                    className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                    Set as {isDropOff ? "Dropped Off" : "Collected"}
                  </button>
                )}
              </dd>
            </div>
            {collectedOn && (
              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="text-sm font-medium text-gray-500">
                  Collected By
                </dt>
                <dd className="items-center text-sm text-gray-900">
                  <div>
                    {lead.data?.data.vehicle?.collectedBy ? <ContactFromId contactId={lead.data?.data.vehicle?.collectedBy} /> : "Unknown"}
                  </div>
                </dd>
              </div>
            )}

            <CODStatus leadId={leadId} />

            <div className="py-2 grid grid-cols-2 gap-4 items-center">
              <dt className="flex items-center text-sm font-medium text-gray-500">
                Status
              </dt>
              <dd className="text-sm text-gray-900">
                <select
                  // defaultValue={lead.status}
                  value={leadStatus}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="border border-gray-400 rounded-md text-sm"
                >
                  {Object.values(LeadStatus).filter((ct) => isNaN(Number(ct))).map((status, i) => (
                    <option key={status} value={i}>
                      {status}
                    </option>
                  ))}
                </select>
              </dd>
            </div>

            <div className="pt-3 flex justify-between items-center">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-x-3 gap-y-3">

                <ButtonInput label="Print Collection Note" classes="" isSubmit={false} onClick={() => printCollectionNote(true)} />

                {lead.data?.data.vehicle?.codCertificateDocument ? (
                  <ButtonInput label="Print COD" classes="" isSubmit={false} onClick={() => printCodCertificate()} />
                ) : (
                  <div> {/* spacer */} </div>
                )}

                <div></div>

                <ButtonInput label="Send Collection Note" classes="" isSubmit={false} onClick={() => sendDocuments(true, false)} />
                {lead.data?.data.vehicle?.codCertificateNumber && (
                  <>
                    <ButtonInput label="Send COD Certificate" classes="" isSubmit={false} onClick={() => sendDocuments(false, true)} />
                    <ButtonInput label="Send Both" classes="" isSubmit={false} onClick={() => sendDocuments(true, true)} />
                  </>
                )}

              </div>
              <div className="flex">
                <ButtonInput label="Complete Lead" classes={lead.data?.data.completedOn && "bg-green-600 hover:bg-green-700"}
                  isSubmit={false} onClick={() => setConfirmComplete(true)}
                />
              </div>
            </div>
          </div>

        </div>
      </Card >
    </>
  );
}
